// @trovex/analytics - the page-view telemetry plugin every internal app can wire
// in, whatever it is built with.
//
// THIS MODULE IMPORTS NOTHING. Not React, not Firebase, not a polyfill. That is
// the whole design property: modules/auth imports `firebase/*` as bare
// specifiers, which is why each of its consumers has to extend its Vite dedupe
// list, and why it can only be used from a bundled app. This one takes a
// `getIdToken` function from the host and therefore works unchanged in a React
// + Vite app and in the vanilla no-build Group Marketing Hub.
//
// An app on a DIFFERENT Firebase project can load this module, but the intake
// cannot verify its token yet: analyticsIngest calls admin.auth().verifyIdToken
// in group-marketing-hub, which rejects a token minted by trovex-technical-hub
// or verify-trovex. The platform already has the tool for it
// (functions/lib/foreign-firebase-token.js) and wiring it in is a branch in one
// function, but until that happens those apps would post into a silent 401.
//
// Wiring, in full:
//
//   import { createTelemetry } from '@trovex/analytics';
//   const telemetry = createTelemetry({
//     appId: 'systems',                                    // platformAccess id
//     getIdToken: () => auth.currentUser?.getIdToken(),
//   });
//   telemetry.trackPageView(window.location.pathname);     // and on route change
//
// Design: docs/superpowers/specs/2026-08-30-platform-user-analytics-design.md

const DEFAULT_ENDPOINT = 'https://europe-west2-group-marketing-hub.cloudfunctions.net/analyticsIngest';

const FLUSH_AT_EVENTS = 20;
// A signed-out tab left open must not grow for ever while it waits for a token.
const MAX_QUEUE = 200;
// THE MOST EVENTS ONE REQUEST MAY CARRY, and it must not exceed the intake's
// own MAX_EVENTS_PER_BATCH or the WHOLE batch is refused with
// `too-many-events`. The queue holds up to MAX_QUEUE, which is deliberately
// larger, so a flush is CHUNKED rather than sent whole.
//
// This was survivable while the queue held only page views - a busy tab lost
// some navigation history. It stopped being survivable the moment client error
// reports started riding in the same queue, because the first thing lost in an
// incident would be the error report about the incident. Exported so the
// functions test can hold the two constants together; nothing in an app should
// read it.
export const MAX_EVENTS_PER_SEND = 50;

// AND BY BYTES, because the count cap alone does not bound a body. Both
// transports refuse a body over 64 KiB - `sendBeacon` by returning false, a
// `keepalive` fetch by rejecting - and `flush` empties the queue before it
// sends, so an over-size chunk is not retried, it is LOST, taking the ordinary
// page views batched alongside it. Well under the limit, because the envelope
// carries an ID token as well as the events.
export const MAX_BYTES_PER_SEND = 48 * 1024;
const FLUSH_EVERY_MS = 30 * 1000;
// A session ends after this long with no page view. Matches the industry
// convention and, more usefully, matches how staff actually work: leaving a tab
// open over lunch should not read as a three-hour session.
const SESSION_IDLE_MS = 30 * 60 * 1000;
// Long enough to swallow a router's repeated effects, short enough that a real
// revisit to the same page still counts.
const REPEAT_WINDOW_MS = 2000;
// ONE LOOPING PAGE MUST NOT BE ABLE TO FLOOD THE ENDPOINT, and a looping page
// is the normal shape of a client error: a render loop throwing inside an
// effect produces thousands of identical errors a minute, not one. TWO
// independent limits, because either alone is escapable. The signature dedupe
// collapses a repeat of the SAME fault to a single report however often it
// fires, which handles the loop. The hard cap bounds a page cycling through
// MANY different faults, which the dedupe alone would let through unbounded.
const MAX_ERRORS_PER_SESSION = 10;
// A message longer than this is truncated before it is queued. The server
// scrubs and re-caps it regardless - this is only so one enormous string
// cannot fill the batch on the way there.
const MAX_ERROR_MESSAGE = 300;
// Firebase ID tokens last an hour. Refreshing well inside that keeps a valid
// token cached for the one moment we cannot await anything - see below.
const TOKEN_REFRESH_MS = 20 * 60 * 1000;
const SESSION_KEY = 'trovexTelemetrySession';

// CONSOLE BREADCRUMBS. The last few things the app said before it threw, sent
// with the error report.
//
// A message and a line number identify WHICH fault; the console says what was
// happening around it, which is usually the difference between reading a fault
// and understanding one. Sentry calls these breadcrumbs and they are the first
// thing anybody looks at.
//
// A RING BUFFER, NOT A STREAM. Nothing is sent unless an error actually fires,
// so an app that never throws sends no console output at all - the same shape
// as recording a replay only on error, and for the same reason.
//
// warn and error only. `console.log` is where apps narrate themselves and is by
// far the densest source of customer values; warn and error are where they say
// something went wrong.
//
// AND ONLY THE FIRST ARGUMENT, AND ONLY IF IT IS A STRING. This is the rule
// that makes the feature safe enough to ship, and it was not obvious.
//
// Breadcrumbs turn every console.warn and console.error call site already in
// the estate into a telemetry payload - 511 of them in voip alone - and not one
// was written with that in mind. scrubErrorMessage removes references, emails,
// urls and digit runs; it does NOT remove customer or company NAMES, because
// nothing can. So
//
//   console.warn('[TechHubPanel] attachment upload failed:', f.name, err)
//
// on a file called "610769 Atlantic Contracts drawing.pdf" scrubs the reference
// and keeps the customer, in a table with a year's retention.
//
// The static first argument is the developer's own sentence and says WHICH
// warning fired, which is nearly all of the diagnostic value. The arguments
// after it are the interpolated values, and that is where the data lives. So
// the arguments are dropped and never sent at all.
//
// RESIDUAL, stated rather than pretended away: an app that builds its message
// by concatenation - console.warn(`failed for ${customer}`) - still puts that
// value in the first argument. It is rarer than passing data as separate
// arguments, and the server still scrubs what arrives.
const BREADCRUMB_LEVELS = ['warn', 'error'];
const MAX_BREADCRUMBS = 10;
// NOT A SLICE. A crumb is kept WHOLE or replaced WHOLE, and the two rejected
// alternatives are each worse in their own direction.
//
// Cutting to the server's own 200 lands the slice inside an identifier and
// leaves a fragment no scrubber pass can match - `...user":"arran.north@tro` -
// which is exactly what scrubErrorMessage's header forbids: nothing is cut
// before it is scrubbed. Cutting at a GENEROUS length, which is what the first
// fix did, does not fix that - it moves the boundary and keeps the defect, and
// it also made each error event carry up to 10 x 2000 characters. Both
// transports cap a body at 64 KiB (`sendBeacon` returns false, a `keepalive`
// fetch rejects), `flush` has already emptied the queue by then, and the whole
// chunk - PAGE VIEWS INCLUDED - is silently lost. Four faults in one window
// was enough.
//
// So: over the limit, the crumb becomes a fixed marker. The sequence still
// shows that a warning fired and where, and no fragment of a customer's data
// is stored. A console line this long is a stringified payload, which is the
// one thing least worth shipping and most likely to carry data.
const MAX_BREADCRUMB_LENGTH = 600;
const CRUMB_TOO_LONG = '[long console output, not recorded]';

// ---------------------------------------------------------------------------
// PRESENCE: the "users active now" heartbeat. Added 2026-08-30.
//
// A page view only fires when somebody NAVIGATES, and flush() returns early on
// an empty queue, so a person reading one page for twenty minutes says nothing
// at all. A live column built on page views alone would show them as gone. So a
// visible, in-use tab says "I am here" every PRESENCE_PING_MS.
//
// TWO CONDITIONS, AND BOTH ARE THE POINT.
//
//  - The tab must be VISIBLE. A background tab is silent, which is most of what
//    stops several open tabs costing several writes. It is not all of it:
//    visibilityState is `visible` in EVERY window, so two windows side by side
//    on a two-monitor desk are both visible at once. A localStorage lease,
//    shared across every tab and window of the origin, claims the ping so only
//    one of them sends it. Best effort by design - if storage is blocked the
//    lease is skipped and the worst case is the pre-lease behaviour, one ping
//    per visible window, which costs writes but never miscounts anybody.
//  - Somebody must have TOUCHED it inside PRESENCE_IDLE_MS, and the signals are
//    `pointerdown`, `keydown`, `wheel` and `touchmove`: gestures a PERSON makes.
//    `scroll` is deliberately absent, because it cannot be told apart from a
//    programmatic one - any page that calls scrollTo on navigation, or restores
//    a scroll position, or animates a section into view, would have refreshed
//    the idle clock by itself for ever and the abandoned-laptop guard would
//    never have fired. A laptop left open overnight on a visible page stops
//    pinging ten minutes after the last real input and drops out of the column
//    five minutes after that.
//
// A closed tab sends no farewell, deliberately: the entry ages out of the
// server's window on its own, so the worst a closed tab can do is read as
// present for a few more minutes, and there is no departure message for a
// dying page to fail to deliver.
//
// It costs one small POST per interval, which writes ONE Firestore entry and
// reads nothing. It never enters the event queue and never becomes an
// analyticsEvents document.
// THESE TWO ARE A CONTRACT WITH THE SERVER, NOT LOCAL PREFERENCES.
// PRESENCE_PING_MS is what the five-minute counting window in
// functions/lib/analytics-presence.js is SIZED AROUND: two and a half pings to
// a window is what lets somebody survive two lost pings without blinking off
// the column. Set this to four minutes and the ratio is gone, and the server
// cannot see that it happened. This module imports nothing, so the link cannot
// be an import: functions/lib/analytics-presence.cases.js declares both values
// and analytics-presence-wiring.test.js reads them back OUT OF THIS SOURCE and
// compares. Change one and that suite goes red.
const PRESENCE_PING_MS = 2 * 60 * 1000;
const PRESENCE_IDLE_MS = 10 * 60 * 1000;
// Shared across every tab and window of this origin, so the ping is claimed
// once per person per app rather than once per visible window. See maybePing.
const PRESENCE_LEASE_KEY = 'trovexTelemetryPing';

// WHAT PEOPLE DO, beside where they go (usage analyst spec, 2026-10-07).
//
// The action names a host may record with track(). A FIXED LIST, mirrored by
// functions/lib/analytics-shape.js UI_ACTIONS, which is what the intake keeps;
// functions/analytics-ui-events.test.js holds the two copies equal. A name not
// on it is dropped here, silently, so a typo in an app records nothing rather
// than something the server would refuse anyway.
export const UI_ACTIONS = Object.freeze([
  'nav',
  'dial',
  'call_answer',
  'call_decline',
  'call_hangup',
  'call_transfer',
  'call_hold',
  'call_mute',
  'outcome_set',
  'notes_saved',
  'feedback_requested',
  'call_scheduled',
  'meeting_scheduled',
  'planner_list_add',
  'queue_run',
  'call_import',
  'contact_add',
  'call_reassign',
  'number_flag',
  'reports_tab',
  'csv_export',
]);
// Per 30-second window, so a stuck loop calling track() cannot fill the queue.
// Both ride the existing sender: neither forces a flush of its own.
export const MAX_ACTIONS_PER_WINDOW = 40;
export const MAX_TAPS_PER_WINDOW = 20;
// One sampled tap at most this often. A heatmap needs the shape of where
// people press, not every press.
export const TAP_MIN_GAP_MS = 1500;
// The same action on the same target this close together is one press that
// fired twice (a click and a keyboard shortcut handler, a double render).
const ACTION_REPEAT_MS = 300;
const MAX_LABEL_CHARS = 60;
// How far up from the pressed node a tap looks for something it can name.
const MAX_TAP_ANCESTORS = 8;
const IMPLICIT_ROLES = {
  BUTTON: 'button', A: 'link', SELECT: 'combobox', TEXTAREA: 'textbox', SUMMARY: 'button',
};
// ---------------------------------------------------------------------------

function randomId() {
  // crypto.randomUUID is not universal (older Safari, insecure origins), and a
  // session id needs to be unique, not unguessable - it identifies nothing on
  // its own and the server never trusts it for identity.
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* fall through */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * A telemetry client for one app.
 *
 * Every failure mode here is a silent no-op. Analytics must never break a page,
 * never block navigation, and never surface an error to a member of staff: an
 * app whose telemetry is misconfigured simply records nothing, and the report
 * shows that app as quiet rather than the app showing an error.
 */
export function createTelemetry({
  appId,
  getIdToken,
  endpoint = DEFAULT_ENDPOINT,
  enabled = true,
  now = () => Date.now(),
  // Share of trusted pointer presses recorded as a ui_tap, 0..1. Off unless a
  // host asks: a heatmap is opt-in per app.
  tapSample = 0,
  // A path the host never records actions or taps on (a customer surface in
  // a staff bundle). Page views are the host's own call and are unaffected.
  ignorePath = null,
  // Where an action or tap is filed, when the host knows better than its last
  // page view: an app that records a dialog as a sub-route of its page knows
  // when the dialog closed again, and this plugin does not.
  uiRoute = null,
  random = Math.random,
} = {}) {
  const noop = {
    trackPageView() {},
    trackError() {},
    track() {},
    flush() { return Promise.resolve(false); },
    stop() {},
    _state: null,
  };
  // No app id or no way to get a token means this app has not been wired up
  // properly; do nothing rather than send unattributable rows.
  if (!enabled || !appId || typeof getIdToken !== 'function') return noop;
  if (typeof window === 'undefined' || typeof document === 'undefined') return noop;

  let queue = [];
  let lastEventAt = 0;
  let sessionId = '';
  let cachedToken = '';
  let tokenFetchedAt = 0;
  let timer = null;
  let stopped = false;
  let lastRoute = '';
  let lastRouteAt = 0;
  let errorsQueued = 0;
  let errorSessionId = '';
  const seenErrors = new Set();
  // Presence state (see PRESENCE_PING_MS above). lastInteractionAt starts at
  // construction: opening the app IS the first interaction, and waiting for a
  // click would leave somebody who lands on a page and reads it invisible.
  let lastInteractionAt = now();
  // The console ring. Held in memory and only ever read when an error fires.
  const breadcrumbs = [];
  const consoleRestore = [];
  // What WE put on the console, so stop() can tell whether ours is still there.
  const patchedConsole = new Map();
  let lastPingAt = 0;
  let actionsThisWindow = 0;
  let tapsThisWindow = 0;
  let lastTapAt = 0;
  let lastActionKey = '';
  let lastActionAt = 0;

  // The session survives a reload (sessionStorage is per tab) so an F5 does not
  // read as a new visit, and rotates after an idle gap.
  function currentSession() {
    const at = now();
    if (sessionId && at - lastEventAt < SESSION_IDLE_MS) return sessionId;
    let stored = null;
    try {
      stored = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null');
    } catch { /* storage blocked or junk: mint a fresh one */ }
    if (stored && stored.id && at - Number(stored.at || 0) < SESSION_IDLE_MS) {
      sessionId = stored.id;
    } else {
      sessionId = randomId();
    }
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id: sessionId, at }));
    } catch { /* storage blocked: the id still works for this page */ }
    return sessionId;
  }

  function touchSession() {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id: sessionId, at: now() }));
    } catch { /* ignore */ }
  }

  // THE TOKEN IS CACHED, AND THAT IS LOAD-BEARING.
  //
  // The flush that matters most happens on pagehide, where nothing can be
  // awaited - the page is going away. So the token is refreshed in the
  // background while the page lives, and the final beacon uses the cached copy.
  // Without this, every closing page would drop its last events, which is
  // precisely the batch carrying how long the visit lasted.
  async function refreshToken() {
    try {
      const token = await getIdToken();
      if (typeof token === 'string' && token) {
        cachedToken = token;
        tokenFetchedAt = now();
        return true;
      }
    } catch { /* signed out, offline, or the host said no: stay silent */ }
    return false;
  }

  // THE TOKEN IS NOT READY WHEN THIS IS CONSTRUCTED.
  //
  // createTelemetry runs while Firebase Auth is still restoring the session, so
  // the first getIdToken resolves null. Without these early retries the token
  // only appeared on the 30-second tick, and anything queued before that - the
  // landing page view of every single visit - was thrown away, along with the
  // whole of any visit shorter than 30 seconds.
  const EARLY_RETRIES_MS = [400, 1200, 3000, 8000];
  function primeToken() {
    refreshToken().then((got) => {
      // The first presence ping goes as soon as there is a token to attribute
      // it to. Waiting for the 30-second tick would leave the person who opened
      // the app missing from a column called "now" for half a minute.
      if (got) maybePing();
      if (got || stopped) return;
      const delay = EARLY_RETRIES_MS.shift();
      if (delay !== undefined) setTimeout(primeToken, delay);
    });
  }

  function send(body, { beacon }) {
    const payload = JSON.stringify(body);
    // text/plain, NOT application/json, and this is not a style choice:
    // sendBeacon cannot perform a CORS preflight, and application/json is not
    // a safelisted content type, so a JSON beacon is dropped by the browser
    // before it leaves. text/plain is safelisted; the server parses the string.
    const type = 'text/plain;charset=UTF-8';
    if (beacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      try {
        if (navigator.sendBeacon(endpoint, new Blob([payload], { type }))) return Promise.resolve(true);
      } catch { /* fall through to fetch */ }
    }
    try {
      return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': type },
        body: payload,
        // Survives the page unloading, which a plain fetch does not.
        keepalive: true,
        // The response carries nothing worth reading (204), and no cookie
        // should ride along - the ID token in the body is the whole identity.
        credentials: 'omit',
        mode: 'cors',
      }).then(() => true).catch(() => false);
    } catch {
      return Promise.resolve(false);
    }
  }

  /**
   * The events split into bodies each transport will actually accept.
   *
   * BOTH CAPS, and the byte one is the one that bites: an error event carries
   * up to ten breadcrumbs, so fifty of them is far past 64 KiB while being
   * exactly at the count limit. A single event over the cap is sent ALONE
   * rather than dropped - the server can refuse it and say so, which is a
   * better outcome than the client silently deciding an error is unreportable.
   */
  function chunkEvents(events) {
    const chunks = [];
    let current = [];
    let bytes = 0;
    for (const event of events) {
      let size;
      try {
        size = JSON.stringify(event).length;
      } catch {
        // Unserialisable: it would fail in send() anyway, and dropping it here
        // stops one bad event taking a whole chunk with it.
        continue;
      }
      if (current.length > 0 && (current.length >= MAX_EVENTS_PER_SEND || bytes + size > MAX_BYTES_PER_SEND)) {
        chunks.push(current);
        current = [];
        bytes = 0;
      }
      current.push(event);
      bytes += size;
    }
    if (current.length > 0) chunks.push(current);
    return chunks;
  }

  async function flush({ beacon = false } = {}) {
    if (queue.length === 0) return false;
    if (!cachedToken) {
      // A beacon fires on a dying page and cannot await anything, so leave the
      // queue where it is - there is no later for this page anyway.
      if (beacon) return false;
      await refreshToken();
    }
    if (!cachedToken) {
      // Still nothing to attribute the batch to. HOLD it for the next tick
      // rather than discarding - auth may simply not be up yet. Only give up
      // once the events are older than a session, which is the point at which
      // posting them under whoever signs in next would be a misattribution
      // rather than a delay.
      if (queue.length && now() - queue[0].ts > SESSION_IDLE_MS) queue = [];
      // And never let a signed-out tab grow without bound.
      if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
      return false;
    }
    // RE-CHECKED AFTER THE AWAIT, not just at entry. `await refreshToken()`
    // suspends, and several auto-flushes can be suspended there at once (one
    // per 20 events queued while signed out). Whichever resumes first drains
    // the queue; the others already passed the entry check, so without this
    // they would reach here with an empty array and POST an empty batch, which
    // the intake refuses as `empty-batch`. It does not reproduce on today's
    // timing - a flush that resumes while the token is still empty bails one
    // branch earlier - but it is a race, not a guarantee, and the guard is one
    // line.
    if (queue.length === 0) return false;
    const events = queue;
    queue = [];
    actionsThisWindow = 0;
    tapsThisWindow = 0;
    const session = currentSession();
    // Chunked, never sent whole: see MAX_EVENTS_PER_SEND. On a dying page each
    // chunk is a separate sendBeacon, which is fine - beacons are queued by the
    // browser and do not need the page to survive.
    const sends = [];
    for (const chunk of chunkEvents(events)) {
      sends.push(send({ token: cachedToken, appId, sessionId: session, events: chunk }, { beacon }));
    }
    return Promise.all(sends).then((results) => results.every(Boolean));
  }

  // PRESENCE. Deliberately separate from the event queue: it carries no events,
  // writes no event document, and a failure to send is simply a ping that did
  // not happen rather than data lost.
  function maybePing() {
    if (stopped || !cachedToken) return;
    if (document.visibilityState !== 'visible') return;
    if (now() - lastInteractionAt > PRESENCE_IDLE_MS) return;
    if (now() - lastPingAt < PRESENCE_PING_MS) return;
    // THE CROSS-WINDOW LEASE. visibilityState is `visible` in every window, so
    // without this a person with the same app open on two monitors pings twice
    // an interval. localStorage is shared across them; the timestamp there is
    // the claim. A race between two windows is harmless (both ping once), and
    // blocked storage simply skips the lease.
    try {
      const key = `${PRESENCE_LEASE_KEY}:${appId}`;
      const claimed = Number(localStorage.getItem(key) || 0);
      const age = now() - claimed;
      // `age >= 0` is what makes the lease SELF-HEALING, and it is not
      // defensive noise. A clock stepped backwards - a laptop waking, an NTP
      // correction, a user changing the date - leaves a claim dated in the
      // future, and a bare `age < PING` test then suppresses every ping for as
      // long as that stamp stands. The only setItem is past the guard, so
      // nothing would ever overwrite it: presence for that person on that app
      // would be dead permanently, silently, on their machine alone. A claim
      // that is not in the past is treated as no claim and reclaimed here.
      if (Number.isFinite(claimed) && age >= 0 && age < PRESENCE_PING_MS) return;
      localStorage.setItem(key, String(now()));
    } catch { /* storage blocked: ping anyway rather than go silent */ }
    lastPingAt = now();
    send({ token: cachedToken, appId, presence: true }, { beacon: false });
  }

  // A PERSON made this, not the page. `isTrusted` is false for anything a
  // script dispatched, and the four events listened for are ones a script
  // cannot cause the browser to raise on its own.
  const onInteraction = (event) => {
    if (event && event.isTrusted === false) return;
    lastInteractionAt = now();
  };

  // -------------------------------------------------------------------------
  // HOW LONG A PAGE TOOK (B8).
  //
  // The programme calls this "the one real blind spot": there was no load-time
  // data at all, so "which pages are slow" had nothing behind it.
  //
  // THREE NUMBERS, MEASURING THREE DIFFERENT THINGS, and keeping them apart is
  // the whole design - the same rule the reporting page already applies to app
  // opens against page views. Adding them together, or reporting one as "load
  // time", would produce a figure that is wrong for every page.
  //
  //   ttfbMs   - request to first byte, from Navigation Timing. THE SERVER's
  //              share, and ours to fix.
  //   loadMs   - navigation start to the load event. The cost of ARRIVING:
  //              bundle, parse, first render. Only the first view of a page
  //              load has one, because an SPA route change is not a navigation
  //              and has no navigation entry. Null on every later view rather
  //              than zero, which would read as instant.
  //   settledMs- route change until the network goes quiet. THE ONE THAT
  //              MATTERS HERE, because this estate's actual pathology is data,
  //              not code: the Dashboard once read 4,119,011 bytes on every
  //              visit and would have reported as fast on any paint-based
  //              measure, since the paint happens first and the wait happens
  //              after it.
  //
  // MEASURED, NOT GUESSED AT: a paint-time metric was considered first and
  // rejected for exactly that reason.
  const QUIET_MS = 500;
  const SETTLE_CAP_MS = 20000;

  /** Navigation Timing for the page load, or nulls. First view only. */
  function navigationTiming() {
    try {
      if (typeof performance === 'undefined' || !performance.getEntriesByType) return null;
      const [nav] = performance.getEntriesByType('navigation');
      if (!nav) return null;
      const load = Math.round(nav.loadEventEnd - nav.startTime);
      const ttfb = Math.round(nav.responseStart - nav.requestStart);
      return {
        // loadEventEnd is 0 until the load event has actually fired, so a view
        // recorded before it would report an instant page. Null instead.
        loadMs: load > 0 ? load : null,
        ttfbMs: ttfb >= 0 && nav.responseStart > 0 ? ttfb : null,
      };
    } catch { return null; }
  }

  /**
   * Time from now until the network goes quiet, handed to `done`.
   *
   * NOT a paint measure. It watches resource timing and calls the page settled
   * once nothing new has loaded for QUIET_MS, which is what catches a route
   * that renders instantly and then spends three seconds fetching.
   *
   * Best effort in every direction: no PerformanceObserver, a browser that does
   * not report resources, or a page that never goes quiet all yield nothing
   * rather than a wrong number. Capped, because a page holding an open stream
   * would otherwise never resolve and would keep a timer alive for the life of
   * the tab.
   */
  function whenSettled(done) {
    let observer = null;
    let quietTimer = null;
    let capTimer = null;
    const startedAt = now();
    let lastActivity = startedAt;
    const finish = (settled) => {
      try { if (observer) observer.disconnect(); } catch { /* already gone */ }
      clearTimeout(quietTimer);
      clearTimeout(capTimer);
      done(settled);
    };
    try {
      if (typeof PerformanceObserver === 'undefined') return () => {};
      const arm = () => {
        clearTimeout(quietTimer);
        quietTimer = setTimeout(() => finish(Math.round(lastActivity - startedAt)), QUIET_MS);
      };
      observer = new PerformanceObserver(() => { lastActivity = now(); arm(); });
      observer.observe({ type: 'resource', buffered: false });
      arm();
      // The cap reports what we had rather than nothing: a page still loading
      // after twenty seconds is the most interesting page on the estate.
      capTimer = setTimeout(() => finish(SETTLE_CAP_MS), SETTLE_CAP_MS);
    } catch { return () => {}; }
    return () => finish(null);
  }

  // TWO LATCHES, NOT ONE, AND THE SINGLE ONE MADE THE WHOLE COLUMN DEAD.
  //
  // `loadMs` needs `loadEventEnd`, which is 0 until the load event has actually
  // fired - and every app calls trackPageView from a mount effect, which
  // commits long before that. The first cut latched on ANY truthy return from
  // navigationTiming(), so the first view recorded ttfb, set the flag, and no
  // later view ever looked again: `load_ms` would have been NULL on 100% of
  // rows for ever, `loadSamples` always 0, and the "First load" column a
  // permanent dash - while the schema, the query and the caption all described
  // a metric that could never populate.
  //
  // ttfb is available immediately and is sent once. loadMs keeps being retried
  // on later views, and - because a session with only ONE view would still
  // never see it - a one-shot `load` listener back-fills the already-queued
  // event, the same mutation the settle watcher does.
  /**
   * Write loadMs onto a queued event when the load event fires.
   *
   * Registered at most once per page load. Without it a session of a SINGLE
   * page view could never report a load time at all, because its only view was
   * recorded before the browser finished loading - every multi-view session is
   * covered by the inline retry on the next view instead.
   *
   * THAT IS WORTH THE COMPLEXITY, AND IT IS MEASURED RATHER THAN ASSUMED. This
   * listener has been got wrong three times in review, so the question of
   * whether to keep it at all was asked properly: over 30 days of the live
   * warehouse, 9 of 26 sessions (34.6%) carried exactly one page view. Dropping
   * it would silently lose the load time for a third of all sessions, and there
   * is no reason to think slow loads are under-represented among them - if
   * anything somebody who opens one page and waits is the case this column
   * exists for.
   *
   * If it ever fails again the failure is SAFE: the panel withholds any load
   * figure drawn from under five samples, so a broken listener leaves the
   * column visibly empty rather than quietly wrong.
   */
  /** The newest page view still in the queue, or null. */
  function lastQueuedView() {
    for (let i = queue.length - 1; i >= 0; i -= 1) {
      if (queue[i] && queue[i].type === 'page_view') return queue[i];
    }
    return null;
  }

  let loadListenerArmed = false;
  let onLoadHandler = null;
  function backfillLoadOnce(event) {
    if (loadListenerArmed || typeof window.addEventListener !== 'function') return;
    loadListenerArmed = true;
    const record = () => {
      try {
        if (sentLoadMs) return;
        const timing = navigationTiming();
        if (!timing || timing.loadMs === null) return;
        // THE NEWEST QUEUED VIEW, not necessarily the one that armed this.
        //
        // Two things are being avoided. Writing onto an event that has already
        // been SENT loses the figure silently and, if the latch is set anyway,
        // blocks every later view from retrying - which dropped precisely the
        // slow loads this column exists to surface. And insisting on the
        // CAPTURED event drops it too when that view has flushed while a newer
        // one is sitting in the queue perfectly able to carry it, which on a
        // slow load is the likely shape.
        //
        // So the latch moves WITH a write to something still queued, and if
        // nothing is queued it stays false and an inline read on the next view
        // picks it up.
        const target = queue.indexOf(event) !== -1
          ? event
          : lastQueuedView();
        if (!target) return;
        target.loadMs = timing.loadMs;
        sentLoadMs = true;
      } catch { /* never cost the app its own load event */ }
    };
    const onLoad = () => {
      try {
        window.removeEventListener('load', onLoad);
        onLoadHandler = null;
        // DEFERRED BY ONE TASK, AND THAT IS THE WHOLE REASON THIS WORKS.
        //
        // `loadEventEnd` is set AFTER the load event has finished dispatching,
        // so read from inside a `load` handler it is still 0 - which
        // navigationTiming correctly reports as null, so the handler returned
        // early, removed itself, and never re-armed. The listener could not
        // fire usefully in any real browser, and the test that "proved" it set
        // the timing BEFORE dispatching, an ordering a browser never produces.
        setTimeout(record, 0);
      } catch { /* never cost the app its own load event */ }
    };
    onLoadHandler = onLoad;
    try { window.addEventListener('load', onLoad); } catch { loadListenerArmed = false; onLoadHandler = null; }
  }

  let sentTtfb = false;
  let sentLoadMs = false;
  // Cancels the in-flight settle watcher when a new route starts, so a fast
  // click-through does not leave one watcher per route running.
  let cancelSettle = null;

  function trackPageView(path, meta) {
    if (stopped) return;
    // Deliberately does NOT refresh the idle clock. A navigation a person made
    // is already covered by the pointerdown or keydown that caused it, while a
    // programmatic redirect - or a router firing its effect again on a
    // re-render - is not a person, and counting it would have kept an
    // unattended page reporting its owner as present for as long as it ran.
    const route = typeof path === 'string' && path ? path : window.location.pathname;
    // React routers fire an effect per render, not per navigation. Deduped on
    // TIME rather than on "is something still queued", which stopped working
    // the moment a flush emptied the queue - a repeat effect just after a
    // flush recorded a second view. A genuine revisit seconds later still
    // counts; only the render storm does not.
    const at = now();
    if (route === lastRoute && at - lastRouteAt < REPEAT_WINDOW_MS) return;
    lastRoute = route;
    lastRouteAt = at;
    currentSession();
    lastEventAt = now();
    touchSession();
    const event = { type: 'page_view', path: route, ts: lastEventAt, ...(meta || {}) };
    // ONCE PER PAGE LOAD. A later route change has no navigation entry of its
    // own, and re-reading the same one would stamp every route in the session
    // with the cost of arriving at the first.
    if (!sentTtfb || !sentLoadMs) {
      const timing = navigationTiming();
      if (timing) {
        if (!sentTtfb && timing.ttfbMs !== null) { event.ttfbMs = timing.ttfbMs; sentTtfb = true; }
        if (!sentLoadMs && timing.loadMs !== null) { event.loadMs = timing.loadMs; sentLoadMs = true; }
      }
      // The load event has not fired yet, and on a one-view session it never
      // will while this event is still queued. Back-fill it when it does.
      if (!sentLoadMs) backfillLoadOnce(event);
    }
    queue.push(event);
    // The settle time is not known yet, so it is written onto the queued object
    // when it arrives. If the batch has already gone the field is simply
    // absent, which is why it is nullable server-side rather than defaulted.
    try { if (cancelSettle) cancelSettle(); } catch { /* nothing in flight */ }
    cancelSettle = whenSettled((settledMs) => {
      cancelSettle = null;
      if (settledMs !== null && settledMs >= 0) event.settledMs = settledMs;
    });
    if (queue.length >= FLUSH_AT_EVENTS) flush();
  }

  // WHAT AN ERROR REPORT IS ALLOWED TO CARRY, decided here rather than left to
  // whatever the browser handed us.
  //
  // A message, the script file, a line and a column. NO STACK, deliberately:
  // a stack is where arbitrary interpolated values end up - a fetch that failed
  // against /platform/610769_FI01 puts a customer order reference in it - and
  // the file and line already identify the fault well enough to find it in a
  // source map. NO `event.error` object either, for the same reason: it can
  // carry a whole response body.
  //
  // The path is sent RAW, exactly as a page view sends it, and the SERVER
  // normalises it to a route pattern before anything is stored. The client is
  // not the right place to enforce that: it is the untrusted half, and the
  // intake already owns `normaliseRoute` as the one definition of what is safe
  // to keep. What the client owns is VOLUME, which the server cannot recover
  // from once a flood has been sent.
  /**
   * The path a ui event is filed under: the host's last recorded view, which
   * may carry a sub-route (a tab or an open dialog), else the address bar.
   */
  function uiPath() {
    if (typeof uiRoute === 'function') {
      try {
        const route = uiRoute();
        if (typeof route === 'string' && route) return route;
      } catch { /* fall back to the last view */ }
    }
    return lastRoute || window.location.pathname;
  }

  function uiIgnored() {
    if (stopped) return true;
    if (typeof ignorePath !== 'function') return false;
    try { return Boolean(ignorePath(window.location.pathname)); } catch { return true; }
  }

  function noteActivity() {
    currentSession();
    lastEventAt = now();
    touchSession();
  }

  /**
   * Record that someone did something: `track('dial', { source: 'hero' })`.
   *
   * `props` is small and flat: ids and enums only. A `target` key in it is
   * taken out as the event's target (the pressed control's data-track id).
   * Never sends by itself; it rides the next 30-second flush.
   */
  function track(action, props) {
    if (uiIgnored()) return;
    if (typeof action !== 'string' || !UI_ACTIONS.includes(action)) return;
    if (actionsThisWindow >= MAX_ACTIONS_PER_WINDOW) return;
    const rest = {};
    let target = '';
    if (props && typeof props === 'object') {
      for (const [key, value] of Object.entries(props)) {
        if (key === 'target') { if (typeof value === 'string') target = value; continue; }
        if (value === undefined || value === null) continue;
        rest[key] = value;
      }
    }
    const at = now();
    const key = `${action}|${target}`;
    if (key === lastActionKey && at - lastActionAt < ACTION_REPEAT_MS) return;
    lastActionKey = key;
    lastActionAt = at;
    noteActivity();
    const event = { type: 'ui_action', action, path: uiPath(), ts: lastEventAt };
    if (target) event.target = target;
    if (Object.keys(rest).length) event.props = rest;
    actionsThisWindow += 1;
    queue.push(event);
  }

  function viewportBucket(width) {
    if (width < 480) return 'xs';
    if (width < 768) return 'sm';
    if (width < 1024) return 'md';
    if (width < 1440) return 'lg';
    return 'xl';
  }

  /**
   * What the pressed thing is, from the node itself up a few ancestors: its
   * data-track id if it has one, else its role and accessible name. NEVER an
   * input's value: an input is named by its aria-label, placeholder or name.
   */
  function describeTarget(node) {
    const out = {};
    let el = node;
    for (let i = 0; el && i < MAX_TAP_ANCESTORS; i += 1, el = el.parentElement) {
      if (typeof el.getAttribute !== 'function') continue;
      const id = el.getAttribute('data-track');
      if (id) { out.target = id; return out; }
      const tag = typeof el.tagName === 'string' ? el.tagName.toUpperCase() : '';
      let role = el.getAttribute('role') || '';
      if (!role && tag === 'INPUT') {
        const type = (el.getAttribute('type') || 'text').toLowerCase();
        role = type === 'checkbox' || type === 'radio' ? type : 'textbox';
      }
      if (!role) role = IMPLICIT_ROLES[tag] || '';
      if (!role) continue;
      out.role = role.toLowerCase();
      let label = el.getAttribute('aria-label') || '';
      if (!label && (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT')) {
        label = el.getAttribute('placeholder') || el.getAttribute('name') || '';
      } else if (!label) {
        label = el.getAttribute('title') || (typeof el.textContent === 'string' ? el.textContent : '');
      }
      label = String(label).replace(/\s+/g, ' ').trim().slice(0, MAX_LABEL_CHARS);
      if (label) out.label = label;
      return out;
    }
    return out;
  }

  const onTap = (event) => {
    try {
      if (!event || event.isTrusted === false) return;
      if (event.isPrimary === false) return;
      if (typeof event.button === 'number' && event.button !== 0) return;
      if (uiIgnored()) return;
      if (tapsThisWindow >= MAX_TAPS_PER_WINDOW) return;
      const at = now();
      if (at - lastTapAt < TAP_MIN_GAP_MS) return;
      if (!(random() < tapSample)) return;
      const width = Number(window.innerWidth) || 0;
      const height = Number(window.innerHeight) || 0;
      if (width <= 0 || height <= 0) return;
      const x = Math.min(1, Math.max(0, Number(event.clientX) / width));
      const y = Math.min(1, Math.max(0, Number(event.clientY) / height));
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;
      lastTapAt = at;
      noteActivity();
      tapsThisWindow += 1;
      queue.push({
        type: 'ui_tap',
        path: uiPath(),
        ts: lastEventAt,
        vp: viewportBucket(width),
        x: Math.round(x * 1000) / 1000,
        y: Math.round(y * 1000) / 1000,
        ...describeTarget(event.target),
      });
    } catch { /* never cost the app its own press */ }
  };

  function errorSignature(message, source, line) {
    return `${message}|${source}|${line}`;
  }

  /**
   * The console line worth keeping, or '' for one that is not.
   *
   * ONLY THE FIRST ARGUMENT, AND ONLY A STRING. See BREADCRUMB_LEVELS above for
   * why: the arguments after the first are the interpolated values, and that is
   * where customer data lives. A call whose first argument is not a string
   * (`console.error(err)`) contributes its message only - an Error's message is
   * the same class of thing as a static sentence.
   *
   * `instanceof Error` is deliberately NOT used: an Error from another realm -
   * an iframe or a worker - fails it, and the object branch would then have
   * produced the useless "{}". Duck-typing on `.message` covers both.
   *
   * Cannot throw. It runs inside a patched console, and a throw here would cost
   * the app its own logging.
   */
  function crumbText(first) {
    try {
      if (typeof first === 'string') return first;
      if (first && typeof first.message === 'string' && first.message) return first.message;
      return '';
    } catch {
      return '';
    }
  }

  /**
   * Patch console.warn and console.error to keep the last few lines.
   *
   * THE ORIGINAL IS ALWAYS CALLED, first and in a try, so a fault in this
   * never costs an app its own logging - a patched console that swallows
   * output would be a far worse bug than the one it was added to diagnose.
   */
  function watchConsole() {
    if (typeof console === 'undefined') return;
    for (const level of BREADCRUMB_LEVELS) {
      const original = console[level];
      if (typeof original !== 'function') continue;
      consoleRestore.push([level, original]);
      const patched = function patchedConsoleFn(...args) {
        try {
          // The FIRST argument only. Everything after it is dropped and never
          // leaves the browser.
          const text = crumbText(args[0]);
          if (text) {
            // Whole, or the marker. Never a slice - see MAX_BREADCRUMB_LENGTH.
            const kept = text.length > MAX_BREADCRUMB_LENGTH ? CRUMB_TOO_LONG : text;
            breadcrumbs.push({ level, at: now(), text: kept });
            if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift();
          }
        } catch { /* never cost the app its own logging */ }
        return original.apply(console, args);
      };
      console[level] = patched;
      // Kept so restoreConsole can tell whether OURS is still the one in place
      // before putting the original back.
      patchedConsole.set(level, patched);
    }
  }

  /**
   * Put the console back, but ONLY if ours is still the one in place.
   *
   * Something else may have wrapped console.warn after this did - a second
   * telemetry instance, a logging library, a devtools shim - and assigning the
   * saved original back unconditionally would silently discard that wrapper.
   * This codebase already records a double-construction incident on
   * window.onerror; the console is a global other code legitimately wraps.
   */
  // A SKIPPED RESTORE IS KEPT, NOT FORGOTTEN. Clearing the bookkeeping
  // unconditionally made a skip permanent: with two instances on one page (A
  // constructed, then B), stopping A finds B's wrapper in place and correctly
  // declines to restore - but then dropped its own record, so B's later restore
  // reinstalled A'S PATCHED FUNCTION as if it were the original. The real
  // console function became unreachable for the life of the page and a stopped
  // instance's ring went on filling. Anything not put back stays on the list
  // for the next stop() to try.
  function restoreConsole() {
    const unrestored = [];
    for (const entry of consoleRestore) {
      const [level, original] = entry;
      try {
        if (console[level] === patchedConsole.get(level)) {
          console[level] = original;
          patchedConsole.delete(level);
        } else {
          unrestored.push(entry);
        }
      } catch { /* nothing to do */ }
    }
    consoleRestore.length = 0;
    for (const entry of unrestored) consoleRestore.push(entry);
  }

  /**
   * A readable message for a rejected promise.
   *
   * `String(reason)` WAS THE BUG. A bare `Promise.reject()` has an undefined
   * reason, `String(undefined)` is the truthy string "undefined", and it sailed
   * through trackError's empty check to become a fault TITLED "undefined" -
   * observed live on Phone. Null did the same. A fault nobody can identify is
   * worse than useless: it groups every reasonless rejection in the estate into
   * one row and tells whoever opens it nothing.
   *
   * Every branch says what actually happened instead, and none of them can
   * throw - this runs inside an error handler.
   */
  function rejectionMessage(event) {
    let reason;
    try { reason = event.reason; } catch { return 'Promise rejected, reason unreadable'; }
    if (reason === undefined) return 'Promise rejected with no reason';
    if (reason === null) return 'Promise rejected with null';
    try {
      if (typeof reason === 'string') return reason || 'Promise rejected with an empty string';
      if (reason.message) return String(reason.message);
      // A DOMException or an HTTP-ish object carries neither, and its type is
      // still more use than "[object Object]".
      if (reason.name) return `Promise rejected: ${String(reason.name)}`;
      if (typeof reason === 'object') {
        const json = JSON.stringify(reason);
        // "{}" is what a plain object with no own enumerable keys serialises
        // to, which identifies nothing.
        return json && json !== '{}' ? `Promise rejected: ${json}` : 'Promise rejected with an object';
      }
      return `Promise rejected: ${String(reason)}`;
    } catch {
      return 'Promise rejected, reason unserialisable';
    }
  }

  /**
   * Report a client-side error. Also exported, so a host with a React error
   * boundary can hand over an error it caught rather than letting it escape to
   * window.onerror purely to be seen.
   */
  function trackError(message, { source = '', line = 0, col = 0, kind = 'error' } = {}) {
    if (stopped) return;
    const text = typeof message === 'string' ? message : String(message || '');
    if (!text) return;
    const clipped = text.slice(0, MAX_ERROR_MESSAGE);
    const file = typeof source === 'string' ? source.slice(0, MAX_ERROR_MESSAGE) : '';
    const signature = errorSignature(clipped, file, line);

    // THE CAP IS PER SESSION, and the counter has to be reset when the session
    // rotates or it is really per TAB. A session ends after 30 minutes idle, so
    // a tab left open for days would otherwise stop reporting client errors
    // permanently after ten distinct faults - silently, and on exactly the
    // long-lived tab most likely to hit an interesting one.
    const session = currentSession();
    if (session !== errorSessionId) {
      errorSessionId = session;
      errorsQueued = 0;
      seenErrors.clear();
    }

    // Same fault again: already reported, and reporting it a thousand more
    // times says nothing new.
    if (seenErrors.has(signature)) return;
    if (errorsQueued >= MAX_ERRORS_PER_SESSION) return;
    seenErrors.add(signature);
    errorsQueued += 1;

    lastEventAt = now();
    touchSession();
    queue.push({
      type: 'client_error',
      // WHAT THE APP SAID JUST BEFORE IT THREW. Read here and nowhere else, so
      // an app that never throws sends no console output at all. Copied rather
      // than referenced, because the ring keeps mutating after this event is
      // queued and a shared array would report whatever happened NEXT.
      crumbs: breadcrumbs.slice(),
      path: window.location.pathname,
      ts: lastEventAt,
      message: clipped,
      source: file,
      line: Number(line) || 0,
      col: Number(col) || 0,
      kind,
    });
    // Flushed on the usual schedule, NOT immediately. An error is not more
    // urgent than the batch it rides in, and flushing on every throw would
    // hand a looping page exactly the request-per-error behaviour the caps
    // above exist to prevent.
    if (queue.length >= FLUSH_AT_EVENTS) flush();
  }

  const onHidden = () => {
    if (document.visibilityState === 'hidden') flush({ beacon: true });
  };
  const onPageHide = () => flush({ beacon: true });
  // `event.message` rather than the error object, per the note above. A
  // cross-origin script reports the opaque "Script error." with no file or
  // line, which is worth having as a count even though it is not diagnosable.
  const onError = (event) => {
    if (!event) return;
    trackError(event.message, {
      source: event.filename, line: event.lineno, col: event.colno, kind: 'error',
    });
  };
  // A rejected promise nobody caught. `reason` is usually an Error but can be
  // anything at all, including nothing.
  const onRejection = (event) => {
    if (!event) return;
    trackError(rejectionMessage(event), { kind: 'unhandledrejection' });
  };

  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  // Passive, and capture, so a handler that stops propagation cannot make a
  // working page look abandoned. Three coarse signals only: this records THAT
  // somebody is at the keyboard, never what they pressed or clicked on.
  const INTERACTIONS = ['pointerdown', 'keydown', 'wheel', 'touchmove'];
  INTERACTIONS.forEach((type) => {
    window.addEventListener(type, onInteraction, { passive: true, capture: true });
  });
  const tapsOn = Number(tapSample) > 0;
  if (tapsOn) window.addEventListener('pointerdown', onTap, { passive: true, capture: true });
  timer = setInterval(async () => {
    // Awaited: firing the refresh and the flush in the same tick meant the
    // flush always ran against the OLD token state.
    if (now() - tokenFetchedAt > TOKEN_REFRESH_MS) await refreshToken();
    // The ui caps are per window: a queue that could not send (no token yet)
    // still opens a fresh allowance each tick rather than going dark for good.
    actionsThisWindow = 0;
    tapsThisWindow = 0;
    flush();
    // On the EXISTING 30-second tick rather than a timer of its own, so a tab
    // that comes back to the front is counted within half a minute while the
    // send itself is still capped at one per PRESENCE_PING_MS.
    maybePing();
  }, FLUSH_EVERY_MS);

  watchConsole();
  primeToken();

  return {
    trackPageView,
    trackError,
    track,
    flush,
    /**
     * The CURRENT session id, so another module can key off the same session
     * this plugin reports under.
     *
     * Public rather than the `_state` test seam, because session replay stores
     * a recording under this id and the warehouse's page_views and
     * client_errors tables both carry it: without one shared id a replay could
     * not be found from the fault it belongs to, which is the whole reason the
     * missing column was worth fixing. It returns the same value the next event
     * would be reported under, minting one if the session has rotated.
     */
    sessionId: () => currentSession(),
    stop() {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', onPageHide);
      restoreConsole();
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
      // The `load` back-fill listener, or a stopped instance keeps a live
      // handler holding this closure and a queued event alive for the life of
      // the page. It is the one listener stop() did not know about, because it
      // is registered lazily rather than at construction.
      if (onLoadHandler) {
        try { window.removeEventListener('load', onLoadHandler); } catch { /* already gone */ }
        onLoadHandler = null;
      }
      INTERACTIONS.forEach((type) => {
        window.removeEventListener(type, onInteraction, { capture: true });
      });
      if (tapsOn) window.removeEventListener('pointerdown', onTap, { capture: true });
      return flush({ beacon: true });
    },
    // Test seam only. Nothing in an app should read this.
    _state: () => ({
      queued: queue.length, sessionId, hasToken: Boolean(cachedToken), errorsQueued, lastPingAt,
      queue: queue.slice(),
    }),
  };
}

export default createTelemetry;
